import React, { useState } from 'react'
import { crmToast } from './crmUi'
import { logDealCall } from './crmApi'

/**
 * LOG A CALL (Lee, 21 Oct 2026) — shared by the lead page and both boards.
 * Outcome is the only required field; description, duration and time are
 * optional. Mirrors the future third-party call-tracking shape, so synced
 * calls will render identically.
 */

const OUTCOMES: Array<{ key: string; label: string; color: string }> = [
  { key: 'connected', label: 'Connected', color: '#10b981' },
  { key: 'no_answer', label: 'No answer', color: '#f59e0b' },
  { key: 'voicemail', label: 'Voicemail', color: '#0ea5e9' },
  { key: 'callback', label: 'Callback arranged', color: '#8b5cf6' },
  { key: 'wrong_number', label: 'Wrong number', color: '#ef4444' },
  { key: 'other', label: 'Other', color: '#64748b' },
]
export const CALL_OUTCOME_META = Object.fromEntries(OUTCOMES.map((o) => [o.key, o])) as Record<string, { key: string; label: string; color: string }>

const localNow = () => {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

export const LogCallModal: React.FC<{ dealId: string; dealName: string; onClose: () => void; onLogged?: () => void }> = ({ dealId, dealName, onClose, onLogged }) => {
  const [outcome, setOutcome] = useState<string | null>(null)
  const [direction, setDirection] = useState<'out' | 'in'>('out')
  const [duration, setDuration] = useState('')
  const [notes, setNotes] = useState('')
  const [at, setAt] = useState(localNow())
  const [busy, setBusy] = useState(false)

  const save = () => {
    if (!outcome) { crmToast('Pick the outcome — everything else is optional', 'error'); return }
    setBusy(true)
    logDealCall(dealId, {
      direction, outcome,
      durationMin: duration.trim() ? Number(duration) : null,
      notes: notes.trim() || null,
      at: at ? new Date(at).toISOString() : null,
    })
      .then(() => { crmToast('Call logged', 'success'); onLogged?.(); onClose() })
      .catch((e: any) => crmToast(e?.message || 'Could not log the call', 'error'))
      .finally(() => setBusy(false))
  }

  const lbl = 'block text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1'
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={onClose}>
      <div className="w-[440px] max-w-full rounded-xl bg-surface border border-border shadow-xl p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
            <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg>
          </span>
          <div className="min-w-0">
            <p className="text-[14px] font-bold text-txt-primary truncate">Log a call — {dealName}</p>
            <p className="text-[11px] text-txt-tertiary">Outcome is required; everything else is optional.</p>
          </div>
        </div>

        <p className={`${lbl} mt-4`}>Outcome</p>
        <div className="grid grid-cols-3 gap-1.5">
          {OUTCOMES.map((o) => (
            <button key={o.key} onClick={() => setOutcome(o.key)}
              className={`px-2 py-2 rounded-lg text-[11px] font-bold border transition-all ${outcome === o.key ? 'text-white shadow-sm' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}
              style={outcome === o.key ? { background: o.color, borderColor: o.color } : undefined}>
              {o.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-2.5 mt-3">
          <span>
            <span className={lbl}>Direction</span>
            <div className="flex rounded-lg border border-border overflow-hidden">
              {(['out', 'in'] as const).map((d) => (
                <button key={d} onClick={() => setDirection(d)}
                  className={`flex-1 px-2 py-1.5 text-[11px] font-bold ${direction === d ? 'bg-brand-600 text-white' : 'bg-surface text-txt-secondary hover:bg-surface-hover'}`}>
                  {d === 'out' ? 'I called' : 'They called'}
                </button>
              ))}
            </div>
          </span>
          <span>
            <span className={lbl}>Minutes</span>
            <input type="number" min={0} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="—"
              className="w-full px-2 py-1.5 rounded-lg border border-border bg-surface text-[12px] text-txt-primary" />
          </span>
          <span>
            <span className={lbl}>When</span>
            <input type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)}
              className="w-full px-2 py-1.5 rounded-lg border border-border bg-surface text-[11px] text-txt-primary" />
          </span>
        </div>

        <p className={`${lbl} mt-3`}>What happened (optional)</p>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
          onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === 'Enter' && save()}
          placeholder="e.g. Spoke to Sarah — wants pricing for 5 users, sending the deck tomorrow… (⌘↵ to save)"
          className="w-full px-2.5 py-2 rounded-lg border border-border bg-surface-secondary text-[12.5px] text-txt-primary outline-none focus:border-brand-500 resize-y" />

        <div className="flex items-center gap-2 mt-4">
          <button className="ml-auto px-3 py-1.5 rounded-lg text-[11px] font-bold border border-border text-txt-secondary hover:bg-surface-hover" onClick={onClose}>Cancel</button>
          <button className="px-4 py-1.5 rounded-lg text-[11px] font-bold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50" disabled={busy || !outcome} onClick={save}>
            {busy ? 'Saving…' : 'Log call'}
          </button>
        </div>
      </div>
    </div>
  )
}
